import { useEffect, useState } from 'react';

import { cn } from '@/_shared/lib/utils';

const HEADER_OPTIONS: string[] = ['Explore data', 'Visualise insights', 'Build audiences'];

const ROTATE_INTERVAL_MS = 3000;
const TRANSITION_MS = 600;
const SLOT_LINE_HEIGHT = 1.2;

function splitFirstWord(title: string): [string, string] {
  const space = title.indexOf(' ');
  if (space === -1) return [title, ''];
  return [title.slice(0, space), title.slice(space)];
}

/**
 * `titles` are the headlines of the tabs the page shows, so the heading never names a hidden tab.
 * With none (no chips on the page), it rotates through all three.
 */
export function ChatWelcomeHeader({
  pinnedTitle,
  titles,
}: {
  pinnedTitle?: string | null;
  titles?: string[];
}): React.ReactElement {
  const [index, setIndex] = useState(0);
  const [animating, setAnimating] = useState(true);
  const options = titles && titles.length > 0 ? titles : HEADER_OPTIONS;
  const pinnedIndex = pinnedTitle ? options.indexOf(pinnedTitle) : -1;
  const pinned = pinnedIndex >= 0;
  const still = pinned || options.length === 1;

  useEffect(() => {
    if (still) return;
    const id = window.setInterval(() => {
      setAnimating(true);
      setIndex((i) => i + 1);
    }, ROTATE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [still]);

  useEffect(() => {
    if (index < options.length) return;
    const snap = window.setTimeout(() => {
      setAnimating(false);
      setIndex(0);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setAnimating(true));
      });
    }, TRANSITION_MS);
    return () => window.clearTimeout(snap);
  }, [index, options.length]);

  const shownIndex = pinned ? pinnedIndex : options.length === 1 ? 0 : Math.min(index, options.length);
  const currentTitle = options[shownIndex % options.length] ?? '';
  const slots: string[] = [...options, options[0]!];

  return (
    <div
      role="heading"
      aria-level={1}
      aria-label={currentTitle}
      className="relative overflow-hidden text-center text-[clamp(1.8rem,3.6vw,2.8rem)] font-medium tracking-[-0.04em]"
      style={{ height: `${SLOT_LINE_HEIGHT}em`, lineHeight: SLOT_LINE_HEIGHT }}
    >
      <div
        className={cn('flex flex-col', animating && 'transition-transform ease-in-out')}
        style={{
          transform: `translateY(-${shownIndex * SLOT_LINE_HEIGHT}em)`,
          transitionDuration: animating ? `${TRANSITION_MS}ms` : undefined,
        }}
        aria-hidden
      >
        {slots.map((option, i) => {
          const [firstWord, rest] = splitFirstWord(option);
          return (
            <div key={i} className="shrink-0" style={{ height: `${SLOT_LINE_HEIGHT}em` }}>
              <span className="text-primary">{firstWord}</span>
              <span className="text-foreground">{rest}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
